import React, { useEffect, useRef, useState } from 'react';
import { Image, Button, Stack, Dropdown } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import PostPhotoGallery from 'components/PostPhotoGallery';
import { S3_URL, USER_TYPES } from 'constants';
import { getCreatedDifferenceInBestUnit, unitNumber } from 'utils';
import useAuthStore from 'store/auth';
import RenderMentions from './RenderMentions';
import useCommonStore, { setModalConfig } from 'store/common';
import {
	logPostLiked,
	useBlockUserMutation,
	useDeleteFeedMutation,
	usePostLikeMutation,
	useReportMutation,
} from 'services';
import toast from 'react-hot-toast';
import {
	removeFeedByPostId,
	removeFeedByUserId,
	setPostLikedUsers,
	updateIsConnected,
	updateIsLike,
	updateTotalLikes,
} from 'store/feeds';
import { useNavigate } from 'react-router-dom';
import useTranslate from 'hooks/useTranslate';
import ConnectBtn from 'components/Common/ActionButton/ConnectBtn';
import FollowBtn from 'components/Common/ActionButton/FollowBtn';
import './styles.scss';

const SocialPost: React.FC<any> = ({ post, refetch, type = 'feeds', show = false }) => {
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const { user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const { mutateAsync: postLikeMutation, isPending } = usePostLikeMutation();
	const { mutateAsync: reportFeedsMutation } = useReportMutation();
	const { mutateAsync: blockUserMutation } = useBlockUserMutation();
	const { mutateAsync: deleteFeedMutation } = useDeleteFeedMutation();

	const {
		postId,
		isConnected,
		isLike,
		profilePicture,
		totalComments,
		totalLikes,
		userId,
		userLocation,
		userName,
		userType,
		isFollowed,
	} = post;

	const [isLiked, setIsLiked] = useState<boolean>(isLike);
	const [likes, setLikes] = useState(Number(totalLikes) || 0);
	const [hasMore, setHasMore] = useState(false);
	const descriptionRef = useRef(null);

	useEffect(() => {
		const element: any = descriptionRef?.current;
		if (element?.scrollHeight > element?.clientHeight) {
			setHasMore(true);
		}
	}, [post?.content]);

	const handleShow = () => {
		if (totalLikes > 0 || likes > 0) {
			setModalConfig({
				visible: true,
				id: postId,
				type: 'postLikedBy',
				onClose: onCloseLikeModal,
				props: {
					size: 'lg',
					dialogClassName: 'dialog-like-list',
				},
			});
		}
	};

	const onCloseLikeModal = () => {
		setPostLikedUsers([]);
		hideCommonModal();
	};

	const onHandleLike = () => {
		postLikeMutation({ postId: post?.postId })
			.then((res: any) => {
				if (res?.success) {
					if (['hash', 'myfeeds'].includes(type)) {
						if (isLiked) setLikes((prev: any) => prev - 1);
						else setLikes((prev: any) => prev + 1);
						setIsLiked((prev) => !prev);
					} else {
						updateIsLike(post?.postId, isLike ? false : true);
						updateTotalLikes(
							postId,
							isLike ? (Number(totalLikes) > 0 ? totalLikes - 1 : 0) : Number(totalLikes) + 1,
						);
					}
				} else {
					setIsLiked((prev) => !prev);
					toast.error(res?.message);
				}
			})
			.catch((err: any) => {})
			.finally(() => {
				logPostLiked(postId);
			});
	};

	const handleConnectBtn = (status: number) => {
		updateIsConnected(postId, status);
	};

	const onCommentClick = () => {
		navigate(`/feed-detail/${postId}#comment-section`);
	};

	const handleModalClick = (payload: any) => {
		reportFeedsMutation(payload)
			.then((res: any) => {
				if (res?.success) {
					removeFeedByPostId(postId);
					// toast.success(t(""))
				}
			})
			.finally(() => {
				setModalConfig({
					visible: false,
					id: postId,
					onClick: handleModalClick,
					type: 'reportFeeds',
					contentType: 'socialPost',
				});
			});
	};

	const blockuser = () => {
		blockUserMutation(userId)
			.then((res: any) => {
				if (res?.success) {
					toast.success(translate('ToastMessage.userBlockedSuccessfully'));
					removeFeedByUserId(userId);
					hideCommonModal();
				}
			})
			.catch((error) => {
				console.error('Failed to block user:', error);
			});
	};

	const blockUserModal = () => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: blockuser,
			data: {
				title: translate('Modal.wantToBlockAccount'),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const onHandleDelete = (id: number | null = null) => {
		setModalConfig({
			visible: true,
			id: id,
			type: 'confirmModal',
			onClick: () => onHandleYes(id),
			data: {
				title: translate('Modal.wantToDelete', { title: `${translate('Modal.thisFeed')}` }),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const onHandleYes = (id: number | null) => {
		deleteFeedMutation(id)
			.then((res: any) => {
				if (res?.success) {
					removeFeedByPostId(Number(id));
					refetch();
				} else {
					toast.error(res?.message);
				}
			})
			.finally(() => {
				hideCommonModal();
			});
	};

	const redirectToProfile = () => {
		navigate(`/${userType === USER_TYPES?.COMPANY ? 'company' : 'seeker'}/profile/${userId}`);
	};

	return (
		<div className="social-post white-box bg-white">
			<Stack className="social-post-inner">
				<div className="social-post-header position-relative d-flex justify-content-between align-items-center">
					<div className="post-profile d-flex flex-column flex-sm-row align-items-baseline align-items-sm-center gap-2 gap-xxl-3">
						<div className="post-profile-img rounded-circle" onClick={() => redirectToProfile()}>
							<Image
								src={
									profilePicture?.length !== 0
										? `${S3_URL}${profilePicture}`
										: userType === USER_TYPES?.COMPANY
											? IMAGE_PATHS.CompanyLogoDefault
											: IMAGE_PATHS.UserDefault
								}
								className="w-100 h-100 object-fit-cover rounded-circle"
								loading="lazy"
								alt="profile-pic"
							/>
						</div>
						<div>
							{userName && (
								<h5 onClick={() => redirectToProfile()} className="fw-bold mb-1">
									{userName}
								</h5>
							)}
							<div className="post-profile-detail d-flex align-items-center flex-wrap">
								{userLocation && <p className="mb-0">{userLocation}</p>}
								{post?.created && (
									<p className="mb-0">{getCreatedDifferenceInBestUnit(post.created)}</p>
								)}
							</div>
						</div>
					</div>

					<div className="post-profile-actions d-flex gap-2 gap-xxl-3">
						{/* {user?.userType !== USER_TYPES?.COMPANY && (
							<>
								{userType === USER_TYPES?.COMPANY && userId !== user?.userId ? (
									<FollowBtn isFollowed={isFollowed} id={userId} follow={'comp'} show={show} />
								) : (
									userId !== user?.userId && (
										<ConnectBtn
											isConnected={isConnected}
											userId={userId}
											handleConnectBtn={handleConnectBtn}
											show={show}
										/>
									)
								)}
							</>
						)} */}
						<div className="edit-action">
							<Dropdown>
								<Dropdown.Toggle
									variant="success"
									id="dropdown-basic"
									className="p-0 rounded-circle"
								>
									<i className="ri-more-2-fill"></i>
								</Dropdown.Toggle>

								<Dropdown.Menu>
									{userId !== user?.userId ? (
										<>
											<Dropdown.Item
												onClick={() => {
													setModalConfig({
														visible: true,
														id: Number(postId),
														onClick: handleModalClick,
														type: 'reportFeeds',
														contentType: 'socialPost',
													});
												}}
											>
												<i className="ri-flag-line"></i> {translate('Report.report')}
											</Dropdown.Item>
											<Dropdown.Item onClick={blockUserModal}>
												<i className="ri-spam-2-line"></i> {translate('Report.block')}
											</Dropdown.Item>
										</>
									) : (
										<>
											<Dropdown.Item
												onClick={() => {
													setModalConfig({
														visible: true,
														id: postId,
														type: 'createNewPost',
														onClose: hideCommonModal,
														props: {
															size: 'lg',
														},
													});
												}}
											>
												<i className="ri-edit-line"></i> {translate('Common.edit')}
											</Dropdown.Item>
											<Dropdown.Item onClick={() => onHandleDelete(postId)}>
												<i className="ri-delete-bin-7-line"></i> {translate('Common.delete')}
											</Dropdown.Item>
										</>
									)}
								</Dropdown.Menu>
							</Dropdown>
						</div>
					</div>
				</div>
				<div className="social-post-body">
					{post?.content && (
						<div className="post-content">
							<p
								className={`description-truncated ${hasMore ? 'has-more' : ''}`}
								ref={descriptionRef}
								onClick={() => navigate(`/feed-detail/${postId}`)}
							>
								<RenderMentions
									content={post?.content}
									taggedUsers={post?.taggedUsers}
									hashTags={post?.hashTags}
								/>
							</p>
						</div>
					)}
					{post?.mediaItems?.length > 0 && <PostPhotoGallery mediaItems={post?.mediaItems} />}
				</div>
				<div className="social-post-actions d-flex gap-1 gap-sm-3">
					<div className="d-flex gap-1 gap-sm-2">
						<Button
							className="gray-transparent-btn post-like-btn rounded-pill"
							disabled={isPending}
							onClick={() => onHandleLike()}
						>
							{['hash', 'myfeeds'].includes(type) ? (
								<i className={`ri-heart-3-${isLiked ? 'fill' : 'line'} me-0`}></i>
							) : (
								<i className={`ri-heart-3-${isLike ? 'fill' : 'line'} me-0`}></i>
							)}
						</Button>
						<Button
							disabled={isPending}
							className="gray-transparent-btn rounded-pill"
							onClick={() => handleShow()}
						>
							{!['hash', 'myfeeds'].includes(type) ? unitNumber(totalLikes, 0) : likes}{' '}
							{translate('Social.likes')}
						</Button>
					</div>
					<Button
						className="gray-transparent-btn rounded-pill"
						onClick={() => {
							onCommentClick();
						}}
					>
						<i className="ri-chat-3-line"></i> {unitNumber(totalComments, 0)}{' '}
						{translate('Social.comments')}
					</Button>
					<Button
						onClick={() => {
							setModalConfig({
								visible: true,
								id: Number(postId),
								type: 'postShare',
							});
						}}
						className="gray-transparent-btn rounded-pill"
					>
						<i className="ri-share-forward-line"></i> {translate('Social.share')}
					</Button>
				</div>
			</Stack>
		</div>
	);
};

export default SocialPost;
